<template>
  <div>
    <q-card
      v-if="!widget.values || widget.values.value == undefined"
      flat
      bordered
      class="q-pa-md"
    >
      <div class="row justify-center">
        <!-- Ensure the title is centered by matching report-status width -->
        <div style="width: 34px;" />
        <q-space />
        <div class="col-auto text-h6">
          {{ title }}
        </div>
        <q-space />
        <report-migratable-button
          v-if="!editing && reportElement.migratable"
          :class="{ 'data-freshness': reportStatus && reportStatus.done }"
        />
        <report-status
          :report-status="reportStatus"
          :class="{ 'data-freshness': reportStatus && reportStatus.done }"
        />
      </div>
      <div class="row justify-center">
        <div class="col-auto text-h5 q-py-xl">
          No Value
        </div>
      </div>
    </q-card>
    <q-card
      v-else
      flat
      bordered
      class="q-pa-md"
    >
      <div class="row justify-center">
        <!-- Ensure the title is centered by matching report-status width -->
        <div style="width: 34px;" />
        <q-space />
        <div class="col-auto text-h6">
          {{ title || widget.title }}
          <div
            class="text-subtitle2 text-grey-8"
          >
            {{ widget.subtitle }}
          </div>
        </div>
        <q-space />
        <report-migratable-button
          v-if="!editing && reportElement.migratable"
          :class="{ 'data-freshness': reportStatus && reportStatus.done }"
          @edit="$emit('edit', true)"
        />
        <report-status
          :report-status="reportStatus"
          :class="{ 'data-freshness': reportStatus && reportStatus.done }"
        />
      </div>

      <div class="row justify-center q-mt-md">
        <div class="col-auto text-h2">
          <i18n-n
            v-if="widget.outputType === 'currency-value'"
            :value="widget.values.value"
            format="currencyNoCents"
            scope="global"
          />
          <i18n-n
            v-else-if="widget.outputType === 'integer-value'"
            :value="widget.values.value"
            format="decimal"
            scope="global"
          />
          <i18n-n
            v-else-if="widget.outputType === 'decimal-e1'"
            :value="widget.values.value"
            format="decimalExactly1"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'money'"
            :value="widget.values.value"
            format="currencyNoCents"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'money-cents'"
            :value="widget.values.value / 100"
            format="currencyNoCents"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'money-cents-e2'"
            :value="widget.values.value / 100"
            format="currency"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'integer-value'"
            :value="widget.values.value"
            format="decimal"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'decimal-1'"
            :value="widget.values.value"
            format="decimal1"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'decimal-e1'"
            :value="widget.values.value"
            format="decimalExactly1"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'decimal-2'"
            :value="widget.values.value"
            format="decimal2"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'decimal-e2'"
            :value="widget.values.value"
            format="decimalExactly2"
            scope="global"
          />
          <i18n-n
            v-else-if="outputColumn.component === 'visit-rating-average'"
            :value="widget.values.value"
            format="decimalExactly2"
            scope="global"
          />
          <div v-else>
            {{ widget.values.value }}
          </div>
        </div>
      </div>
      <div
        v-if="widget.values.count !== undefined"
        class="q-mb-md text-overline"
      >
        from {{ widget.values.count }} review{{ widget.values.count === 1 ? '' : 's' }}
      </div>
      <div
        v-else
        class="q-mb-md"
      />

      <div
        v-if="!onlyShowWidgetValue"
      >
        <template
          v-if="!hidePriorIndicators"
        >
          <div class="row justify-between">
            <div
              v-for="prior in priors"
              :key="prior.name"
              class="col-auto column items-center"
            >
              <div
                class="col text-overline"
                style="font-size: 10px"
              >
                Prior {{ prior.name }}
              </div>
              <div
                class="col text-h6"
                :class="'text-' + prior.color"
              >
                <q-icon
                  :name="prior.icon"
                  :color="prior.color"
                />
                {{ prior.percent }} %
              </div>
            </div>
          </div>
        </template>
        <div>
          <highcharts
            ref="chart"
            :options="widgetHighchartOptions"
            class="full-width"
          />
        </div>
      </div>
    </q-card>
  </div>
</template>

<script>
import { isComponentSourceCents } from 'src/models/report/ReportElement'
import ReportMigratableButton from 'src/modules/admin/report/ReportMigratableButton'
import ReportStatus from 'src/modules/admin/report/ReportStatus'

export default {
  name: 'Widget',
  components: {
    ReportStatus,
    ReportMigratableButton
  },
  props: {
    reportElement: {
      type: Object,
      required: true
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['edit'],
  computed: {
    widget () {
      return this.reportElement.widget
    },

    seriesList () {
      const seriesList = this.widget.sparkline.chartData.series

      _.each(seriesList, series => {
        if (this.isMoneyOutput) {
          series.tooltip = {
            valueDecimals: 2,
            valuePrefix: this.currentCircuit?.localeCurrencySymbol || '$'
          }
        } else if (this.isPercentOutput) {
          series.tooltip = {
            valueDecimals: 0,
            valueSuffix: ' %'
          }
        } else if (this.isIntegerValue) {
          series.tooltip = {
            valueDecimals: 0
          }
        }

        if (isComponentSourceCents(this.outputColumn?.component) && (!series.convertedToCents)) {
          series.data = _.map(series.data, point => {
            return [
              point[0],
              point[1] / 100
            ]
          })
          series.convertedToCents = true
        }
      })

      return _.reverse(seriesList)
    },
    isMoneyOutput () {
      return this.outputColumn && (_.includes(this.outputColumn.component, 'money'))
    },
    isPercentOutput () {
      return this.outputColumn && _.includes(this.outputColumn.component, 'percent')
    },
    isIntegerValue () {
      return this.outputColumn && _.includes(this.outputColumn.component, 'integer-value')
    },
    reportStatus () {
      return this.reportElement.reportStatus
    },
    reportOptions () {
      return this.reportElement.reportOptions
    },
    onlyShowWidgetValue () {
      return this.reportOptions.only_show_number
    },
    hidePriorIndicators () {
      return this.reportOptions.hide_prior_indicators
    },
    title () {
      return this.reportOptions.has_custom_title ? this.reportOptions.custom_title : this.widget.title
    },
    priors () {
      return _(['month', 'week', 'day']).map(period => {
        const value = this.widget.values.value
        const priorValue = this.widget.values['prior_' + period + '_value']

        if (priorValue === undefined) return

        let direction, color, icon
        if (priorValue === 0 || Math.round(value) === Math.round(priorValue)) {
          direction = 0
          color = 'grey'
          icon = 'dash'
        } else if (value > priorValue) {
          direction = 1
          color = 'positive'
          icon = 'keyboard_arrow_up'
        } else {
          direction = -1
          color = 'negative'
          icon = 'keyboard_arrow_down'
        }

        return {
          name: _.startCase(period),
          percent: !priorValue ? 0 : Math.round(Math.abs(value - priorValue) / priorValue * 100),
          direction,
          color,
          icon
        }
      }).compact().value()
    },
    outputColumn () {
      return _.find(this.reportElement.columns, { field: this.reportElement.finalOptions.output_type }) || {}
    },
    widgetHighchartOptions () {
      return {
        chart: {
          type: 'line',
          height: 75
        },
        legend: {
          enabled: false
        },
        xAxis: {
          type: 'datetime',
          visible: false,
          labels: {
            enabled: false
          },
          title: {
            text: null
          },
          startOnTick: false,
          endOnTick: false,
          tickPositions: [
            0
          ],
          tickLength: 0
        },
        yAxis: {
          endOnTick: false,
          startOnTick: false,
          labels: {
            enabled: false
          },
          title: {
            text: null
          },
          tickPositions: [
            0
          ],
          tickLength: 0
        },
        title: {
          text: null
        },
        credits: {
          enabled: false
        },
        tooltip: {
          valueDecimals: 2
        },
        series: this.seriesList
      }
    }

  }
}
</script>

<style lang="scss" scoped>
</style>
